import React from "react";
import TrainingList from "./TrainingList";
import TrainingDetail from "./TrainingDetail";
import TrainingProgress from "./TrainingProgress";

/**
 * TrainingMobile - Mobile-specific shell for the Training feature.
 *
 * Renders a mobile-optimised header:
 *  - Title row with an inline icon-only search toggle
 *  - Full-width, stretch search bar when expanded
 *  - Full-width Browse / My Progress tab strip below the title
 *
 * For the detail view, passes isMobile={true} to TrainingDetail so it can
 * render a compact sticky back bar, condensed meta row, and full-width
 * action buttons appropriate for a narrow screen.
 *
 * All state and event handlers are owned by the parent Training.js and passed
 * in as props so this component stays a pure presentation layer.
 */
const TrainingMobile = ({
  currentView,
  selectedTraining,
  selectedSession,
  searchExpanded,
  searchQuery,
  activeSearchTerm,
  searchInputRef,
  searchContainerRef,
  setCurrentView,
  setSearchQuery,
  setSearchExpanded,
  handleSelectTraining,
  handleBackToList,
  handleSearch,
  handleSearchKeyPress,
  clearSearch,
  handleTrainingComplete,
}) => {
  // Detail view is rendered directly (no page header wrapper).
  if (currentView === "detail" && selectedTraining) {
    return (
      <TrainingDetail
        trainingUuid={selectedTraining.uuid}
        sessionId={selectedSession}
        onBack={handleBackToList}
        onComplete={handleTrainingComplete}
        isMobile={true}
      />
    );
  }

  return (
    <div className="common-container training-container">
      {/* ── Mobile Header ── */}
      <div className="common-header">
        <div className="training-header-wrapper">
          {/* Row 1: title + search icon toggle */}
          <div className="training-mobile-title-row">
            <div className="training-title-section">
              <h1 className="main-title">Training Center</h1>
            </div>

            {/* Search icon — only visible when search is collapsed */}
            {currentView === "list" && !searchExpanded && !activeSearchTerm && (
              <button
                className="training-search-icon-btn"
                onClick={() => setSearchExpanded(true)}
                title="Search trainings"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </button>
            )}
          </div>

          {/* Row 2: expanded search bar (full-width) */}
          {currentView === "list" && (searchExpanded || activeSearchTerm) && (
            <div
              className="training-search-container training-search-container-mobile"
              ref={searchContainerRef}
            >
              <div className="training-search-row">
                <div className="training-search-input-wrapper">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search trainings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="training-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="training-search-clear-btn"
                      title="Clear input"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6L6 18" />
                        <path d="M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  className="btn btn-primary training-search-btn training-search-submit-btn"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>

              {activeSearchTerm && (
                <div className="training-search-criteria">
                  <span className="training-search-criteria-label">
                    Search:
                  </span>
                  <span className="training-search-criteria-term">
                    {activeSearchTerm}
                  </span>
                  <button
                    className="training-search-criteria-clear"
                    onClick={clearSearch}
                    title="Clear search criteria"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Row 3: Browse / My Progress tab strip (full-width) */}
          <div className="training-nav-tabs training-nav-tabs-mobile">
            <button
              className={`training-nav-tab ${currentView === "list" ? "active" : ""}`}
              onClick={() => setCurrentView("list")}
            >
              Browse
            </button>
            <button
              className={`training-nav-tab ${currentView === "progress" ? "active" : ""}`}
              onClick={() => setCurrentView("progress")}
            >
              My Progress
            </button>
          </div>
        </div>
      </div>

      {/* ── Content Area ── */}
      <div className="training-page-content">
        {currentView === "list" && (
          <TrainingList
            onSelectTraining={handleSelectTraining}
            searchQuery={searchQuery}
          />
        )}
        {currentView === "progress" && (
          <TrainingProgress onSelectTraining={handleSelectTraining} />
        )}
      </div>
    </div>
  );
};

export default TrainingMobile;
